import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Play, CheckCircle2, XCircle, Pencil, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

export const APPT_STATUS = {
    scheduled: { el: 'Προγραμματισμένο', en: 'Scheduled', cls: 'bg-indigo-100 text-indigo-700' },
    in_progress: { el: 'Σε εξέλιξη', en: 'In progress', cls: 'bg-sky-100 text-sky-700' },
    completed: { el: 'Ολοκληρώθηκε', en: 'Completed', cls: 'bg-green-100 text-green-700' },
    cancelled: { el: 'Ακυρώθηκε', en: 'Cancelled', cls: 'bg-slate-100 text-slate-500' },
};

const CompleteDialog = ({ open, onOpenChange, el, onSubmit }) => {
    const [cost, setCost] = useState('');
    const [mileage, setMileage] = useState('');
    const [saving, setSaving] = useState(false);
    const submit = async () => { setSaving(true); await onSubmit({ cost: cost ? Number(cost) : null, mileage: mileage ? Number(mileage) : null }); setSaving(false); };
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="bg-white max-w-sm" data-testid="appt-complete-dialog">
                <DialogHeader><DialogTitle>{el ? 'Ολοκλήρωση ραντεβού' : 'Complete appointment'}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <div className="space-y-1"><Label>{el ? 'Κόστος (€)' : 'Cost (€)'}</Label><Input type="number" min="0" value={cost} onChange={(e) => setCost(e.target.value)} data-testid="appt-cost" /></div>
                    <div className="space-y-1"><Label>{el ? 'Χιλιόμετρα' : 'Mileage'}</Label><Input type="number" min="0" value={mileage} onChange={(e) => setMileage(e.target.value)} data-testid="appt-mileage" /></div>
                    <Button onClick={submit} disabled={saving} className="w-full bg-green-600 hover:bg-green-700 text-white rounded-sm" data-testid="appt-complete-submit">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (el ? 'Ολοκλήρωση' : 'Complete')}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export const AppointmentActions = ({ a, el, onEdit, onChanged }) => {
    const [completing, setCompleting] = useState(false);
    const setStatus = (status, extra = {}) => api.post(`/workshop/appointments/${a.id}/status`, { status, ...extra })
        .then(() => { toast.success(el ? 'Αποθηκεύτηκε' : 'Saved'); setCompleting(false); onChanged(); })
        .catch((e) => toast.error(e.response?.data?.detail || 'Error'));
    const remove = () => window.confirm(el ? 'Διαγραφή ραντεβού;' : 'Delete appointment?') && api.delete(`/workshop/appointments/${a.id}`).then(onChanged);
    const open = ['scheduled', 'in_progress'].includes(a.status);
    return (
        <div className="flex flex-wrap gap-1">
            {a.status === 'scheduled' && <Button size="sm" variant="outline" className="text-sky-700" onClick={() => setStatus('in_progress')} data-testid={`appt-start-${a.id}`}><Play className="w-4 h-4 mr-1" />{el ? 'Έναρξη' : 'Start'}</Button>}
            {open && <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => setCompleting(true)} data-testid={`appt-complete-${a.id}`}><CheckCircle2 className="w-4 h-4 mr-1" />{el ? 'Ολοκλήρωση' : 'Complete'}</Button>}
            {open && <Button size="sm" variant="ghost" className="text-red-600" onClick={() => setStatus('cancelled')} data-testid={`appt-cancel-${a.id}`}><XCircle className="w-4 h-4 mr-1" />{el ? 'Ακύρωση' : 'Cancel'}</Button>}
            {onEdit && <Button size="sm" variant="ghost" className="text-[#1B7EB5]" onClick={onEdit} data-testid={`appt-edit-${a.id}`}><Pencil className="w-4 h-4" /></Button>}
            <Button size="sm" variant="ghost" className="text-red-500" onClick={remove} data-testid={`appt-delete-${a.id}`}><Trash2 className="w-4 h-4" /></Button>
            <CompleteDialog open={completing} onOpenChange={setCompleting} el={el} onSubmit={(extra) => setStatus('completed', extra)} />
        </div>
    );
};

export const AppointmentCard = ({ a, el, onEdit, onChanged }) => {
    const s = APPT_STATUS[a.status] || APPT_STATUS.scheduled;
    return (
        <div className="fleet-card p-4 flex flex-col gap-3" data-testid={`appt-card-${a.id}`}>
            <div className="flex flex-wrap items-center gap-2">
                <p className="font-semibold text-slate-800">{a.date.split('-').reverse().join('/')}{a.time ? ` · ${a.time}` : ''}</p>
                <span className="font-semibold text-slate-700">{a.license_plate}</span>
                {a.vehicle && <span className="text-sm text-slate-500">{a.vehicle}</span>}
                <span className={`text-xs px-2 py-0.5 rounded-sm ${s.cls}`} data-testid={`appt-status-${a.id}`}>{el ? s.el : s.en}</span>
            </div>
            <p className="text-sm text-slate-700">{a.service_type} · {el ? 'Πελάτης' : 'Customer'}: {a.customer_name}</p>
            {(a.cost != null || a.mileage != null) && <p className="text-xs text-slate-500">{a.cost != null ? `€${a.cost}` : ''}{a.mileage != null ? ` · ${a.mileage} km` : ''}</p>}
            {a.notes && <p className="text-xs text-slate-500">{a.notes}</p>}
            <AppointmentActions a={a} el={el} onEdit={onEdit} onChanged={onChanged} />
        </div>
    );
};
